import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, TextField } from '@mui/material';
import { apiErrorMessage } from '../../../api/http';
import { FormError, ModalActions } from '../modalStyles';

export interface IEventoFormData {
	title: string;
	description: string;
	date: string;
}

interface AnadirEventoModalProps {
	open: boolean;
	defaultDate: string;
	initialData?: IEventoFormData;
	onClose: () => void;
	onConfirmar: (data: IEventoFormData) => void | Promise<void>;
}

const AnadirEventoModal: React.FC<AnadirEventoModalProps> = ({
	open,
	defaultDate,
	initialData,
	onClose,
	onConfirmar,
}) => {
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [date, setDate] = useState(defaultDate);
	const [error, setError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const isEditMode = !!initialData;

	useEffect(() => {
		if (!open) {
			setTitle('');
			setDescription('');
			setDate(defaultDate);
			setError(null);
			setSaving(false);
			return;
		}
		setTitle(initialData?.title ?? '');
		setDescription(initialData?.description ?? '');
		setDate(initialData?.date ?? defaultDate);
		setError(null);
		setSaving(false);
		// initialData is read only when the dialog opens, so typing does not reset the fields.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const handleClose = () => {
		if (saving) return;
		onClose();
	};

	const canSave = title.trim().length > 0 && description.trim().length > 0 && date.length > 0;

	const handleConfirmar = async () => {
		if (!canSave || saving) return;
		setSaving(true);
		setError(null);
		try {
			await onConfirmar({ title: title.trim(), description: description.trim(), date });
		} catch (err) {
			setError(apiErrorMessage(err));
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			<DialogTitle>{isEditMode ? 'Editar evento' : 'Añadir evento'}</DialogTitle>
			<DialogContent>
				<TextField
					fullWidth
					label='Título *'
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					margin='normal'
					autoFocus
					slotProps={{ htmlInput: { maxLength: 160 } }}
				/>
				<TextField
					fullWidth
					label='Descripción *'
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					margin='normal'
					multiline
					rows={3}
					slotProps={{ htmlInput: { maxLength: 2000 } }}
				/>
				<TextField
					fullWidth
					label='Fecha *'
					type='date'
					value={date}
					onChange={(e) => setDate(e.target.value)}
					margin='normal'
					slotProps={{ inputLabel: { shrink: true } }}
				/>
				{error && <FormError severity='error'>{error}</FormError>}
			</DialogContent>
			<ModalActions>
				<Button variant='outlined' onClick={handleClose} disabled={saving}>
					Cancelar
				</Button>
				<Button variant='contained' onClick={() => void handleConfirmar()} disabled={!canSave || saving}>
					{saving ? 'Guardando...' : isEditMode ? 'Guardar cambios' : 'Añadir'}
				</Button>
			</ModalActions>
		</Dialog>
	);
};

export default AnadirEventoModal;
